import type { ConsoleTab, MissionId, MwRevealCursor } from '@badger-sentinel/rules';
import type { OrderDraft, OrderTargetWire } from '@badger-sentinel/protocol';
import { create } from 'zustand';

import {
  EMPTY_DRAFT,
  fromOrderDraft,
  toOrderDraft,
  type BuilderDraft,
  type ParamValue,
  type SlotId,
} from '../model/builder.js';
import { paramSpecOf } from '../model/missions.js';
import {
  moveOrder,
  nextQueueKey,
  queueOf,
  reorderQueue,
  resetQueueKeys,
  type QueuedOrder,
  type SentDraft,
} from '../model/queue.js';
import {
  DEFAULT_CYBER_LAYERS,
  DEFAULT_MAP_LAYERS,
  DEFAULT_SPACE_LAYERS,
  type CyberLayerKey,
  type CyberLayers,
  type MapLayerKey,
  type MapLayers,
  type SpaceLayerKey,
  type SpaceLayers,
} from '../model/map-layers.js';
import { defaultRailWidth, RAIL_MAX_WIDTH, RAIL_MIN_WIDTH } from '../model/rails.js';
import {
  defaultOpFor,
  nextFacetState,
  NO_FACETS,
  type FacetSelection,
  type FacetState,
  type FilterPredicate,
} from '../model/filter.js';
import {
  columnByKey,
  newPredicate,
  resetPredicateKeys,
  type RecognitionColumn,
} from '../model/recognition.js';
import type { MwSortId } from '../model/mw.js';
import type { Selection } from '../model/selection.js';

/**
 * Console UI state — one store, independent of the snapshot (M5-D9).
 *
 * `CellView` is replaced wholesale on every resolution and every intra-turn
 * change (M2-D6). Anything driven by it flickers on every turn advance, and
 * anything *stored* in it is lost on every turn advance. So which tab you are
 * looking at, which alerts you dismissed, **what you have selected**, what you
 * have pinned and how wide you dragged the rails live here and survive the swap
 * — shell.md's "shell state survives a snapshot swap", made structural rather
 * than remembered.
 *
 * Phase 1 populated the chrome's share of the ~40 keys §3 anticipates. Phase 2
 * added selection, rails, filters, pins and the compare tray. Phase 3 adds the
 * three task slots, the queue, the sent-draft manifest, the confirm and the undo
 * stack.
 *
 * ## The queue is the client's whole set, and it has a turn
 *
 * `zOrdersDraftPayload` is never a delta, so the client must hold everything it
 * wants stored. A turn advance therefore clears it — last turn's orders are not
 * this turn's — and a seat that reloads mid-window **adopts** what the server
 * echoed back, because otherwise the next edit would send a set with the earlier
 * orders missing and quietly delete them.
 *
 * ## Three lifetimes, deliberately different
 *
 * | | Reset on seat switch | Persisted across reload |
 * | --- | --- | --- |
 * | Tab, alerts, toast, **selection, pins, filters, batch** | yes | no |
 * | Rail open state and widths | **no** | **yes** |
 * | Dropped-snapshot count | no | no |
 *
 * How wide someone drags a rail is a fact about a person and a screen, not about
 * a seat — the same reasoning that keeps the theme in `localStorage`. What is
 * selected, filtered and pinned is a fact about a seat's picture, and carrying
 * it across a seat switch would be shell.md must-not #3 in miniature.
 */

export interface ViewerRef {
  readonly team: string;
  readonly cell: string;
}

export interface ToastState {
  readonly text: string;
  readonly tone: 'ok' | 'warn';
  /** Distinguishes two identical messages, so the second one restarts the timer. */
  readonly id: number;
}

/** Right-click, resolved to a subject. The four the design draws (design 1616–1633). */
/** `band` is Phase 6's fifth: an orbital ring, which is a zone the ground has no name for. */
/**
 * `network` is Phase 7's, and it is a **zone** in everything but the lookup: the
 * design gives one `region` context branch three tables to resolve an id against
 * (`regionById`, `regimeById`, `ROUTERS.find`) and one set of items. It is its
 * own subject here for the reason `band` is — `map.regions` has no `BLUE-CORE`
 * in it, so an enclave arriving as a `zone` finds nothing and the menu refuses
 * to open on something the operator is looking at.
 */
export type ContextSubject = 'unit' | 'zone' | 'band' | 'network' | 'order' | 'canvas';

export interface ContextMenuState {
  readonly subject: ContextSubject;
  /** The row, region or order the menu was opened on. Empty for `canvas`. */
  readonly id: string;
  readonly x: number;
  readonly y: number;
}

/**
 * A pending imperative request to the map — reveal a unit or a zone, reset the
 * view, fit to a side.
 *
 * A request rather than a call, because the map may not be mounted when it is
 * made: `F` on another tab, and every one of these from a context menu the shell
 * owns. The design's own bridge covers the same gap by re-`querySelector`ing the
 * element for up to four seconds; carrying it in the store makes the tab switch
 * and the reveal one state change, and needs no timer.
 *
 * `seq` is what makes two requests for the same thing distinct, and what the map
 * clears itself against.
 */
export type MapCommandRequest =
  | { readonly kind: 'UNIT'; readonly id: string }
  | { readonly kind: 'REGION'; readonly id: string }
  /** An orbital band. `focusRegime` on the SDA board, and nothing on the theatre. */
  | { readonly kind: 'BAND'; readonly id: string }
  /**
   * A cyber enclave. `focusRouter` on the cyber board, and nothing elsewhere.
   *
   * The id is **ours** — the network — and `maps/CyberMap.tsx` turns it into the
   * router the renderer keys its segments on. Carrying the router here would put
   * the renderer's vocabulary in the store, where two other boards can see it.
   */
  | { readonly kind: 'NETWORK'; readonly id: string }
  | { readonly kind: 'RESET' }
  | { readonly kind: 'FIT'; readonly side: 'BLU' | 'OPF' };

export type MapCommand = MapCommandRequest & { readonly seq: number };

export interface ConsoleState {
  /** The seat this console belongs to. Set from `session.hello`, never from a view. */
  seat: ViewerRef | null;
  selectedTab: ConsoleTab | null;
  hotkeysOpen: boolean;
  dismissedAlerts: ReadonlySet<string>;
  toast: ToastState | null;
  /**
   * Snapshots refused by the viewer guard. A non-zero value here is a bug of the
   * exact kind shell.md's second must-not exists to catch, so it is counted
   * rather than swallowed.
   */
  droppedSnapshots: number;

  // --- rails -------------------------------------------------------------
  railLeftOpen: boolean;
  railRightOpen: boolean;
  /** The width the operator asked for. `railLayout` decides what it gets. */
  railLeftWidth: number;
  railRightWidth: number;

  // --- left rail ---------------------------------------------------------
  search: string;
  filterOpen: boolean;
  facets: FacetSelection;
  /** Group ids the operator has collapsed. Absent means open — groups start open. */
  closedGroups: ReadonlySet<string>;
  regionsOpen: boolean;

  // --- selection and the tray -------------------------------------------
  selection: Selection | null;
  pins: readonly Selection[];
  compareOpen: boolean;
  /** The batch. Ids only; `applyBatch` turns one draft into one order per member. */
  batch: readonly string[];

  contextMenu: ContextMenuState | null;

  // --- the maps ----------------------------------------------------------
  /** What the canvas draws on top of the picture. Never what it places (M5-D11). */
  mapLayers: MapLayers;
  /**
   * The orbital board's four, kept separately because the two canvases draw
   * different things — the design keys them apart for the same reason
   * (`spaceLayers`, 2522–2524).
   */
  spaceLayers: SpaceLayers;
  /**
   * The cyber board's two (`cyberLayers`, design 1943 and 2523). A third table
   * rather than a generalisation, for the reason `model/map-layers.ts` gives at
   * its third caller.
   */
  cyberLayers: CyberLayers;
  /**
   * One key for all three boards, because the design has one control.
   *
   * The LAYERS button lives inside the taskable pane's single canvas region and
   * only the `x-import` under it swaps (markup 343–370), so two maps are never
   * on screen together and a second key could only record which of two identical
   * panels the operator last had open.
   */
  mapLayerPanelOpen: boolean;
  /**
   * Terrain or vector basemap — the *seed*, not the live answer.
   *
   * The live one is `window.BS_MAP_TERRAIN`, because the canvas carries its own
   * TER/VEC button and writes that global directly. The wrapper seeds the element
   * from here on mount and reads the global back on unmount, so the choice
   * survives a tab switch without this key pretending to be in charge of it.
   *
   * **It opens on vector, and the design opens on terrain.** Terrain imagery is
   * the one thing this console fetches from a third party (Q23), and REGION MAP
   * is the tab every seat lands on — so the design's default makes first paint on
   * every seat depend on `services.arcgisonline.com`. Q23's own third
   * consequence is the argument: *"a console that looks different in the room
   * than it did the day before is a console somebody distrusts at the wrong
   * moment"*, and a default that cannot vary removes that difference instead of
   * watching for it. TER is one click away and the choice is remembered.
   */
  mapTerrain: boolean;
  /**
   * A terrain tile failed to load, so the basemap is vector whether or not it was
   * asked to be (Q23's accepted degradation, made visible rather than silent).
   */
  mapTerrainAvailable: boolean;
  /**
   * The id the cursor is resting on, in the rail **or** on the map.
   *
   * One key for both directions: the rail and the map are two views of one list,
   * so whichever one the cursor is over rings the other. It is set on
   * pointer enter and leave only — never on move — so it costs two renders a
   * hover and not sixty.
   */
  hoverId: string | null;
  /** `F`, FOCUS ON MAP, ZOOM TO ZONE, RESET VIEW, FIT TO … — see `MapCommand`. */
  mapCommand: MapCommand | null;

  // --- the order builder ------------------------------------------------
  builder: BuilderDraft;
  builderOpen: boolean;
  /** Which slot's picker is open, and what has been typed into it. */
  armedSlot: SlotId | null;
  pickQuery: string;
  /** The builder's full-ladder overlay (design 1545–1600). */
  ladderOpen: boolean;

  // --- the queue --------------------------------------------------------
  queue: readonly QueuedOrder[];
  /** The turn the queue belongs to. A different one clears it. */
  queueTurn: number | null;
  /**
   * Bumped by every **local** edit and by nothing else.
   *
   * The send effect watches it, so adopting the server's echo cannot trigger a
   * send back to the server — which would be a loop that looks like a working
   * console right up until the window closes.
   */
  queueRevision: number;
  dockOpen: boolean;
  /** The queue card whose success breakdown is open (design 372–420). */
  whyKey: string | null;
  /** Sent drafts still awaiting an echo, newest last. */
  sends: readonly SentDraft[];

  // --- missile warning ---------------------------------------------------
  /** The launch event the board is working. Turn-scoped: last turn's track is gone. */
  mwSelected: string | null;
  mwSort: MwSortId;
  /**
   * The identity the seat has picked but not yet asserted, per event.
   *
   * Not the same thing as `track.workingIdentity`, which is what the *server*
   * holds after an IDENTIFY. Keeping the two apart is what lets the panel show
   * an asserted identity beside the one about to replace it, rather than
   * silently overwriting one with the other.
   */
  mwIdentityDraft: Readonly<Record<string, string>>;
  /** The detail panel's full-signature toggle (design 931). */
  mwFullSignature: boolean;
  mwTimelineOpen: boolean;
  /** The recognition table overlay and its written predicates. */
  mwRefOpen: boolean;
  mwFilters: readonly FilterPredicate[];
  mwFiltersOpen: boolean;
  /** The catalogue row whose note is expanded. One at a time. */
  mwRowOpen: string | null;
  /**
   * A stage has landed on a tab the operator is not looking at.
   *
   * Phase 1 refused to draw the tab strip's attention dot because nothing could
   * raise it, and a marker that never lights is worse than no marker. Stage
   * arrivals are the first thing that can, and this is the only key that lights
   * it.
   */
  mwAttention: boolean;

  /**
   * The turn the intel reader is showing, or `null` for **follow the newest**.
   *
   * Seat-scoped rather than turn-scoped, and that is the whole decision: an
   * operator who has walked back three turns is reading history deliberately, and
   * a resolution landing under them must not yank the page to the new report.
   * The reader names the turn it is showing at the top of the pane and `→` walks
   * back to the newest, so nothing is lost by staying put — whereas being moved
   * mid-sentence is exactly the loss of place `TrackPanel` exists to prevent.
   *
   * Clamped on read (`readerTurn`) rather than on write: a cursor is a number and
   * the newest turn moves, so the clamp belongs where the pair is known.
   */
  intelTurn: number | null;
  /** Groups the operator has folded away, by category. Same shape as the roster's. */
  intelClosedGroups: ReadonlySet<string>;
  intelDeltaOpen: boolean;
  /**
   * A report has arrived for a turn this operator has not opened the tab on.
   *
   * The tab strip takes a *set* of tabs wanting attention and knows nothing about
   * which module raised it, so this needed no change there. Two of Phase 4's three
   * rules carry over — raise only on a new arrival, clear on tab entry — and the
   * **third does not**: `phase.changed` clears `mwStages` because a stage belongs
   * to the window that produced it, but a report does not go away when the next
   * window opens, and the report and the phase change arrive together. Clearing on
   * `phase.changed` would put the dot out in the same instant it was lit.
   */
  intelAttention: boolean;
  /** The newest report turn this seat has been shown, so a re-render cannot re-raise. */
  intelSeenTurn: number | null;

  confirm: ConfirmRequest | null;
  undo: readonly UndoSnapshot[];

  setSeat(seat: ViewerRef | null): void;
  selectTab(tab: ConsoleTab): void;
  setHotkeysOpen(open: boolean): void;
  dismissAlert(key: string): void;
  showToast(text: string, tone?: 'ok' | 'warn'): void;
  clearToast(id: number): void;
  noteDroppedSnapshot(): void;

  toggleRail(side: 'left' | 'right'): void;
  setRailWidth(side: 'left' | 'right', width: number): void;

  setSearch(search: string): void;
  setFilterOpen(open: boolean): void;
  cycleFacet(facet: string, value: string): void;
  clearFacets(): void;
  toggleGroup(group: string): void;
  setRegionsOpen(open: boolean): void;

  select(selection: Selection | null): void;
  togglePin(selection: Selection): void;
  unpin(id: string): void;
  setCompareOpen(open: boolean): void;
  toggleBatch(id: string): void;
  clearBatch(): void;

  openContextMenu(menu: ContextMenuState): void;
  closeContextMenu(): void;

  toggleMapLayer(layer: MapLayerKey): void;
  toggleSpaceLayer(layer: SpaceLayerKey): void;
  toggleCyberLayer(layer: CyberLayerKey): void;
  setMapLayerPanelOpen(open: boolean): void;
  setMapTerrain(on: boolean): void;
  noteTerrainUnavailable(): void;
  setHover(id: string | null): void;
  commandMap(command: MapCommandRequest): void;
  clearMapCommand(seq: number): void;

  setBuilderOpen(open: boolean): void;
  armSlot(slot: SlotId | null): void;
  setPickQuery(query: string): void;
  setLadderOpen(open: boolean): void;
  fillSlot(slot: 'asset', value: string): void;
  fillMission(mission: MissionId): void;
  fillTarget(target: OrderTargetWire): void;
  fillParam(key: string, value: ParamValue): void;
  clearSlot(slot: SlotId): void;
  setBuilder(draft: BuilderDraft): void;

  syncTurn(turn: number): void;
  adoptAccepted(turn: number, accepted: readonly OrderDraft[]): void;
  queueDraft(): boolean;
  /** One mission and one target across `eligible`. Returns how many queued. */
  applyBatch(eligible: readonly string[]): number;
  editOrder(key: string): void;
  duplicateOrder(key: string): void;
  reassignOrder(key: string): void;
  cancelOrder(key: string): void;
  cancelOrdersFor(subjectId: string): void;
  moveQueued(key: string, direction: -1 | 1): void;
  dropQueued(fromKey: string, toKey: string): void;
  setDockOpen(open: boolean): void;
  setWhyKey(key: string | null): void;

  noteSend(send: SentDraft): void;
  pruneSends(throughSeq: number | null): void;

  selectMwTrack(eventId: string | null): void;
  setMwSort(sort: MwSortId): void;
  setMwIdentityDraft(eventId: string, identity: string): void;
  setMwFullSignature(open: boolean): void;
  setMwTimelineOpen(open: boolean): void;
  setMwRefOpen(open: boolean): void;
  setMwFiltersOpen(open: boolean): void;
  addMwFilter(column?: string): void;
  setMwFilter(id: string, patch: Partial<Omit<FilterPredicate, 'id'>>): void;
  removeMwFilter(id: string): void;
  clearMwFilters(): void;
  setMwRowOpen(id: string | null): void;
  raiseMwAttention(): void;
  clearMwAttention(): void;

  readIntelTurn(turn: number | null): void;
  toggleIntelGroup(category: string): void;
  setIntelDeltaOpen(open: boolean): void;
  noteIntelReport(turn: number, looking: boolean): void;
  clearIntelAttention(): void;

  requestConfirm(confirm: ConfirmRequest): void;
  clearConfirm(): void;

  undoLast(): string | null;
}

/**
 * A confirm the operator has been asked for but not yet given.
 *
 * One key, not one per site: two confirms open at once is a console asking two
 * questions and taking one answer. Phase 4's MW RELEASE joins this union.
 */
export type ConfirmRequest =
  | { readonly kind: 'cancel-order'; readonly key: string }
  | { readonly kind: 'cancel-orders'; readonly subject: string }
  | { readonly kind: 'batch-apply' }
  /**
   * Phase 4's two, and the pair `ConfirmInline` was shaped against: irreversible,
   * timed, and priced. `stage` is carried so the confirm's detail line can name
   * the credit *as it stood when the question was asked* — a stage landing while
   * the operator reads the confirm must not silently change the number they are
   * answering about.
   */
  | { readonly kind: 'mw-release'; readonly eventId: string; readonly stage: MwRevealCursor }
  | { readonly kind: 'mw-dismiss'; readonly eventId: string; readonly stage: MwRevealCursor }
  /**
   * The two irreversible acts in the chrome, which had no guard at all until
   * now while cancelling one queued order — trivially reversible — had one.
   *
   * `LEAVE` is a 24px ghost button forty pixels from the theme toggle, and it
   * gives up the seat mid-window. `CLOSE WINDOW` is styled identically to
   * `PAUSE`, sits ten pixels from `+1 MIN`, and ends the window for all sixteen
   * players at once. Neither is reversible by the person who pressed it.
   */
  | { readonly kind: 'leave-seat' }
  | { readonly kind: 'gm-force-close' };

/**
 * One step of undo (design's `pushUndo`).
 *
 * Slot state **and** queue state, because every gesture in this phase moves both
 * — queueing empties the builder, editing refills it from the queue — and an
 * undo that restored one of the two would leave the console in a state the
 * operator never saw.
 */
export interface UndoSnapshot {
  readonly label: string;
  readonly builder: BuilderDraft;
  readonly queue: readonly QueuedOrder[];
}

/** The design keeps 24. Enough for a whole window, bounded so a long turn cannot grow it forever. */
const UNDO_DEPTH = 24;

let toastSeq = 0;

/**
 * Rail geometry, persisted (`bs-rails`).
 *
 * Written on every change and read once at module load. Corrupt or stale JSON is
 * discarded rather than repaired — the fallback is the design's own default, and
 * a rail one build wide is not worth a migration.
 */
const RAILS_KEY = 'bs-rails';

interface StoredRails {
  readonly leftOpen: boolean;
  readonly rightOpen: boolean;
  readonly leftWidth: number;
  readonly rightWidth: number;
}

function viewportWidth(): number {
  return typeof window === 'undefined' ? 1920 : window.innerWidth;
}

function defaultRails(): StoredRails {
  const width = viewportWidth();
  return {
    leftOpen: true,
    rightOpen: true,
    leftWidth: defaultRailWidth('left', width),
    rightWidth: defaultRailWidth('right', width),
  };
}

function isWidth(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function readRails(): StoredRails {
  const fallback = defaultRails();
  if (typeof localStorage === 'undefined') return fallback;
  try {
    const raw = localStorage.getItem(RAILS_KEY);
    if (raw === null) return fallback;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return fallback;
    const stored = parsed as Partial<StoredRails>;
    return {
      leftOpen: typeof stored.leftOpen === 'boolean' ? stored.leftOpen : fallback.leftOpen,
      rightOpen: typeof stored.rightOpen === 'boolean' ? stored.rightOpen : fallback.rightOpen,
      leftWidth: isWidth(stored.leftWidth) ? clampWidth(stored.leftWidth) : fallback.leftWidth,
      rightWidth: isWidth(stored.rightWidth) ? clampWidth(stored.rightWidth) : fallback.rightWidth,
    };
  } catch {
    return fallback;
  }
}

function clampWidth(width: number): number {
  return Math.max(RAIL_MIN_WIDTH, Math.min(RAIL_MAX_WIDTH, Math.round(width)));
}

function writeRails(rails: StoredRails): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(RAILS_KEY, JSON.stringify(rails));
  } catch {
    // A console with no storage still works; it just forgets its furniture.
  }
}

const initialRails = readRails();

/**
 * Everything a seat switch clears. Named once so it cannot drift from `setSeat`.
 *
 * A factory rather than a constant: the two `Set`s would otherwise be one
 * instance shared by every reset, which works only for as long as nobody writes
 * to a set in place.
 */
function seatScoped() {
  return {
    selectedTab: null,
    dismissedAlerts: new Set<string>(),
    toast: null,
    search: '',
    filterOpen: false,
    facets: NO_FACETS,
    closedGroups: new Set<string>(),
    regionsOpen: true,
    selection: null,
    pins: [] as readonly Selection[],
    compareOpen: false,
    batch: [] as readonly string[],
    contextMenu: null,
    mapLayers: DEFAULT_MAP_LAYERS,
    spaceLayers: DEFAULT_SPACE_LAYERS,
    cyberLayers: DEFAULT_CYBER_LAYERS,
    mapLayerPanelOpen: false,
    mapTerrain: false,
    mapTerrainAvailable: true,
    hoverId: null,
    mapCommand: null,
    ...turnScoped(),
    builderOpen: true,
    dockOpen: true,
    // Furniture, like the builder strip and the dock: which sort the operator
    // reads the rail in, and whether the timeline is up, are not facts about a
    // turn.
    mwSort: 'NEXT' as MwSortId,
    mwTimelineOpen: true,
    mwRefOpen: false,
    mwFilters: [] as readonly FilterPredicate[],
    mwFiltersOpen: false,
    // The reader's furniture. Which turn is open and which groups are folded are
    // facts about a seat's reading, not about a turn — a report does not go away
    // when the next one arrives, so neither does the operator's place in it.
    intelTurn: null as number | null,
    intelClosedGroups: new Set<string>(),
    intelDeltaOpen: true,
    intelAttention: false,
    intelSeenTurn: null as number | null,
  };
}

/**
 * Everything a **turn advance** clears, which is a shorter list than a seat
 * switch and a different one.
 *
 * The queue goes because the draft is per turn and the server has already
 * resolved it. Whether the builder strip and the dock are *open* does not: that
 * is furniture, and a console that folded itself up every five minutes would be
 * unusable by turn three.
 */
function turnScoped() {
  return {
    builder: EMPTY_DRAFT,
    armedSlot: null as SlotId | null,
    pickQuery: '',
    ladderOpen: false,
    queue: [] as readonly QueuedOrder[],
    queueTurn: null as number | null,
    queueRevision: 0,
    whyKey: null as string | null,
    sends: [] as readonly SentDraft[],
    confirm: null as ConfirmRequest | null,
    undo: [] as readonly UndoSnapshot[],
    // A track belongs to the window that produced it, and so does the attention
    // it raised: carrying either into the next turn would point the operator at
    // a board that no longer exists.
    mwSelected: null as string | null,
    mwIdentityDraft: {} as Readonly<Record<string, string>>,
    mwFullSignature: false,
    mwRowOpen: null as string | null,
    mwAttention: false,
  };
}

export const useConsoleStore = create<ConsoleState>((set, get) => ({
  seat: null,
  hotkeysOpen: false,
  droppedSnapshots: 0,
  railLeftOpen: initialRails.leftOpen,
  railRightOpen: initialRails.rightOpen,
  railLeftWidth: initialRails.leftWidth,
  railRightWidth: initialRails.rightWidth,
  ...seatScoped(),

  setSeat: (seat) =>
    set((state) => {
      const same =
        state.seat !== null &&
        seat !== null &&
        state.seat.team === seat.team &&
        state.seat.cell === seat.cell;
      if (same) return state;
      // shell.md must-not #3: never retain a previous seat's view. Switching
      // seats resets everything the previous seat chose, because "which tab was
      // I on" and "what did I have selected" are facts about a seat and not
      // about a person. The rails are the exception, and are not in this set.
      return { seat, ...seatScoped() };
    }),

  selectTab: (tab) => set({ selectedTab: tab }),
  setHotkeysOpen: (hotkeysOpen) => set({ hotkeysOpen }),

  dismissAlert: (key) =>
    set((state) => ({ dismissedAlerts: new Set([...state.dismissedAlerts, key]) })),

  showToast: (text, tone = 'ok') => set({ toast: { text, tone, id: ++toastSeq } }),
  clearToast: (id) => set((state) => (state.toast?.id === id ? { toast: null } : state)),

  noteDroppedSnapshot: () => set((state) => ({ droppedSnapshots: state.droppedSnapshots + 1 })),

  toggleRail: (side) => {
    const state = get();
    const next =
      side === 'left'
        ? { railLeftOpen: !state.railLeftOpen }
        : { railRightOpen: !state.railRightOpen };
    set(next);
    persist(get());
  },

  setRailWidth: (side, width) => {
    set(side === 'left' ? { railLeftWidth: width } : { railRightWidth: width });
    persist(get());
  },

  setSearch: (search) => set({ search }),
  setFilterOpen: (filterOpen) => set({ filterOpen }),

  cycleFacet: (facet, value) =>
    set((state) => {
      const current: Readonly<Record<string, FacetState>> = state.facets[facet] ?? {};
      const next = nextFacetState(current[value] ?? 'OFF');
      const values: Record<string, FacetState> = { ...current };
      // `OFF` is stored as absence, so `armedCount` never has to skip anything
      // and an all-off facet leaves no residue behind to confuse `matchesFacets`.
      if (next === 'OFF') delete values[value];
      else values[value] = next;
      const facets: Record<string, Readonly<Record<string, FacetState>>> = { ...state.facets };
      if (Object.keys(values).length === 0) delete facets[facet];
      else facets[facet] = values;
      return { facets };
    }),

  clearFacets: () => set({ facets: NO_FACETS }),

  toggleGroup: (group) =>
    set((state) => {
      const closed = new Set(state.closedGroups);
      if (closed.has(group)) closed.delete(group);
      else closed.add(group);
      return { closedGroups: closed };
    }),

  setRegionsOpen: (regionsOpen) => set({ regionsOpen }),

  // Selecting anything opens the rail that shows it: a click that produced no
  // visible result is a console that looks broken.
  select: (selection) =>
    set(selection === null ? { selection: null } : { selection, railRightOpen: true }),

  togglePin: (selection) =>
    set((state) => {
      const pinned = state.pins.some((pin) => pin.id === selection.id);
      return {
        pins: pinned
          ? state.pins.filter((pin) => pin.id !== selection.id)
          : [...state.pins, selection],
      };
    }),

  unpin: (id) =>
    set((state) => {
      const pins = state.pins.filter((pin) => pin.id !== id);
      return pins.length === 0 ? { pins, compareOpen: false } : { pins };
    }),

  setCompareOpen: (compareOpen) => set({ compareOpen }),

  toggleBatch: (id) =>
    set((state) => ({
      batch: state.batch.includes(id)
        ? state.batch.filter((member) => member !== id)
        : [...state.batch, id],
    })),

  clearBatch: () => set({ batch: [] }),

  openContextMenu: (contextMenu) => set({ contextMenu }),
  closeContextMenu: () =>
    set((state) => (state.contextMenu === null ? state : { contextMenu: null })),

  // --- the theatre map ---------------------------------------------------

  toggleMapLayer: (layer) =>
    set((state) => ({ mapLayers: { ...state.mapLayers, [layer]: !state.mapLayers[layer] } })),
  toggleSpaceLayer: (layer) =>
    set((state) => ({ spaceLayers: { ...state.spaceLayers, [layer]: !state.spaceLayers[layer] } })),
  toggleCyberLayer: (layer) =>
    set((state) => ({ cyberLayers: { ...state.cyberLayers, [layer]: !state.cyberLayers[layer] } })),
  setMapLayerPanelOpen: (mapLayerPanelOpen) => set({ mapLayerPanelOpen }),
  setMapTerrain: (mapTerrain) => set({ mapTerrain }),
  // One way only. A tile service that answered once and then stopped is still a
  // tile service the operator cannot rely on for this session, and a flag that
  // flickered back would take the notice off the screen without the terrain
  // coming back with it.
  noteTerrainUnavailable: () =>
    set((state) =>
      state.mapTerrainAvailable ? { mapTerrainAvailable: false, mapTerrain: false } : state,
    ),
  setHover: (hoverId) => set((state) => (state.hoverId === hoverId ? state : { hoverId })),
  commandMap: (command) =>
    set((state) => ({
      mapCommand: { ...command, seq: (state.mapCommand?.seq ?? 0) + 1 } as MapCommand,
      // Revealing a zone is selecting it: the design's own `focusUnit` sets `sel`
      // before it asks the map for anything, so the right rail is already open on
      // the subject by the time the canvas finishes travelling to it. A unit is
      // already selected by whatever asked for the reveal.
      selection:
        command.kind === 'REGION'
          ? { kind: 'REGION' as const, id: command.id }
          : command.kind === 'BAND'
            ? { kind: 'BAND' as const, id: command.id }
            : state.selection,
    })),
  // Cleared by sequence, so a request made while the map was travelling to the
  // last one is not thrown away by the arrival of the first.
  clearMapCommand: (seq) =>
    set((state) => (state.mapCommand?.seq === seq ? { mapCommand: null } : state)),

  // --- the builder ------------------------------------------------------

  setBuilderOpen: (builderOpen) =>
    set((state) =>
      builderOpen
        ? { builderOpen, railRightOpen: true }
        : { builderOpen, armedSlot: null, pickQuery: '', ladderOpen: state.ladderOpen },
    ),

  // Arming a slot opens the strip *and* the rail that holds it: a keystroke whose
  // result is off screen is the same lie as a control that does nothing.
  armSlot: (armedSlot) =>
    set({
      armedSlot,
      pickQuery: '',
      ...(armedSlot === null ? {} : { builderOpen: true, railRightOpen: true }),
    }),

  setPickQuery: (pickQuery) => set({ pickQuery }),
  setLadderOpen: (ladderOpen) => set({ ladderOpen }),

  fillSlot: (_slot, value) =>
    set((state) => ({
      ...pushUndo(state, 'SET ASSET'),
      builder: { ...state.builder, asset: value },
      armedSlot: null,
      pickQuery: '',
      builderOpen: true,
    })),

  // Changing the mission drops the parameter with it: a payload chosen for
  // Deploy Payload is not a salvo size for Intercept, and carrying it across
  // would send a key the new mission's resolver never reads.
  fillMission: (mission) =>
    set((state) => ({
      ...pushUndo(state, 'SET MISSION'),
      builder: { ...state.builder, mission, params: {} },
      armedSlot: paramSpecOf(mission)?.required === true ? 'param' : null,
      pickQuery: '',
      builderOpen: true,
    })),

  fillTarget: (target) =>
    set((state) => ({
      ...pushUndo(state, 'SET TARGET'),
      builder: { ...state.builder, target },
      armedSlot: null,
      pickQuery: '',
      builderOpen: true,
    })),

  fillParam: (key, value) =>
    set((state) => ({
      ...pushUndo(state, 'SET PARAMETER'),
      builder: { ...state.builder, params: { ...state.builder.params, [key]: value } },
      armedSlot: null,
      pickQuery: '',
    })),

  clearSlot: (slot) =>
    set((state) => {
      const builder =
        slot === 'asset'
          ? { ...state.builder, asset: null }
          : slot === 'mission'
            ? { ...state.builder, mission: null, params: {} }
            : slot === 'target'
              ? { ...state.builder, target: null }
              : { ...state.builder, params: {} };
      return { ...pushUndo(state, 'CLEAR SLOT'), builder };
    }),

  setBuilder: (builder) =>
    set((state) => ({ ...pushUndo(state, 'BUILDER'), builder, builderOpen: true })),

  // --- the queue --------------------------------------------------------

  // The single owner of `queueTurn`. A queue that was never stamped could not be
  // cleared, and a seat whose first echo is `orders: null` — which is every fresh
  // turn — would carry last turn's orders into this one.
  syncTurn: (turn) =>
    set((state) => (state.queueTurn === turn ? {} : { ...turnScoped(), queueTurn: turn })),

  /**
   * Take the server's stored set as the queue.
   *
   * Only when this seat has sent nothing and holds nothing this turn — a reload,
   * or a resnapshot after a long gap. Adopting on every echo would fight the
   * local queue, and adopting over a non-empty one would silently undo an edit
   * the operator made a moment ago.
   */
  adoptAccepted: (turn, accepted) =>
    set((state) => {
      // An echo for a turn that has already turned over is not this turn's set.
      if (state.queueTurn !== null && state.queueTurn !== turn) return {};
      if (state.sends.length > 0) return {};
      if (state.queue.length > 0) return {};
      if (accepted.length === 0) return state.queueTurn === turn ? {} : { queueTurn: turn };
      return { queue: queueOf(accepted), queueTurn: turn };
    }),

  queueDraft: () => {
    const order = toOrderDraft(get().builder);
    if (order === null) return false;
    set((state) => ({
      ...pushUndo(state, 'QUEUE ORDER'),
      queue: [...state.queue, { key: nextQueueKey(), order }],
      queueRevision: state.queueRevision + 1,
      builder: EMPTY_DRAFT,
      armedSlot: null,
      pickQuery: '',
      ladderOpen: false,
      dockOpen: true,
    }));
    return true;
  },

  /**
   * One mission, one target, every batched asset (the design's `batchApply`).
   *
   * Each pairing becomes its own order and the server validates each on its own.
   * The count returned is what queued, so the caller can say so.
   */
  applyBatch: (eligible) => {
    const state = get();
    const mission = state.builder.mission;
    if (mission === null) return 0;
    const params = state.builder.params;
    // The batch holds whatever was ⌘-clicked, which includes contacts, regions
    // and networks. Only the caller has a view to say which of them this seat can
    // actually task, so it hands the eligible subset in — the alternative is
    // sending `asset: 'CTC-…'` to be refused as ASSET_NOT_FOUND.
    const made = eligible.map((asset) => ({
      key: nextQueueKey(),
      order: {
        asset,
        mission,
        target: state.builder.target ?? { kind: 'NONE' as const },
        ...(Object.keys(params).length === 0 ? {} : { params }),
      },
    }));
    if (made.length === 0) return 0;
    set((current) => ({
      ...pushUndo(current, 'BATCH'),
      queue: [...current.queue, ...made],
      queueRevision: current.queueRevision + 1,
      builder: EMPTY_DRAFT,
      batch: current.batch.filter((member) => !eligible.includes(member)),
      armedSlot: null,
      dockOpen: true,
      confirm: null,
    }));
    return made.length;
  },

  // Editing pulls the order back into the builder rather than opening a second
  // editor: there is one place an order is composed, and it is the same place
  // every time.
  editOrder: (key) =>
    set((state) => {
      const entry = state.queue.find((candidate) => candidate.key === key);
      if (entry === undefined) return {};
      return {
        ...pushUndo(state, 'EDIT ORDER'),
        queue: state.queue.filter((candidate) => candidate.key !== key),
        queueRevision: state.queueRevision + 1,
        builder: fromOrderDraft(entry.order),
        builderOpen: true,
        armedSlot: null,
        whyKey: null,
      };
    }),

  duplicateOrder: (key) =>
    set((state) => {
      const index = state.queue.findIndex((candidate) => candidate.key === key);
      const entry = state.queue[index];
      if (entry === undefined) return {};
      const next = [...state.queue];
      next.splice(index + 1, 0, { key: nextQueueKey(), order: entry.order });
      return {
        ...pushUndo(state, 'DUPLICATE ORDER'),
        queue: next,
        queueRevision: state.queueRevision + 1,
      };
    }),

  // Keep the two decisions already made and arm only the asset slot.
  reassignOrder: (key) =>
    set((state) => {
      const entry = state.queue.find((candidate) => candidate.key === key);
      if (entry === undefined) return {};
      return {
        ...pushUndo(state, 'REASSIGN ORDER'),
        queue: state.queue.filter((candidate) => candidate.key !== key),
        queueRevision: state.queueRevision + 1,
        builder: { ...fromOrderDraft(entry.order), asset: null },
        builderOpen: true,
        armedSlot: 'asset' as SlotId,
        pickQuery: '',
        whyKey: null,
      };
    }),

  cancelOrder: (key) =>
    set((state) => ({
      ...pushUndo(state, 'CANCEL ORDER'),
      queue: state.queue.filter((candidate) => candidate.key !== key),
      queueRevision: state.queueRevision + 1,
      confirm: null,
      whyKey: state.whyKey === key ? null : state.whyKey,
    })),

  cancelOrdersFor: (subjectId) =>
    set((state) => ({
      ...pushUndo(state, 'CANCEL ORDERS'),
      queue: state.queue.filter(
        (candidate) =>
          candidate.order.asset !== subjectId && !targetNames(candidate.order, subjectId),
      ),
      queueRevision: state.queueRevision + 1,
      confirm: null,
    })),

  moveQueued: (key, direction) =>
    set((state) => {
      const queue = moveOrder(state.queue, key, direction);
      if (queue === state.queue) return {};
      return { ...pushUndo(state, 'REORDER'), queue, queueRevision: state.queueRevision + 1 };
    }),

  dropQueued: (fromKey, toKey) =>
    set((state) => {
      const queue = reorderQueue(state.queue, fromKey, toKey);
      if (queue === state.queue) return {};
      return { ...pushUndo(state, 'REORDER'), queue, queueRevision: state.queueRevision + 1 };
    }),

  setDockOpen: (dockOpen) => set({ dockOpen }),
  setWhyKey: (whyKey) =>
    set((state) => ({ whyKey, dockOpen: whyKey === null ? state.dockOpen : true })),

  noteSend: (send) => set((state) => ({ sends: [...state.sends, send] })),

  pruneSends: (throughSeq) =>
    set((state) =>
      throughSeq === null ? {} : { sends: state.sends.filter((send) => send.seq >= throughSeq) },
    ),

  // --- missile warning ---------------------------------------------------

  // Selecting a track clears any confirm standing over it: the question was about
  // the track the operator was looking at, and they are not looking at it any
  // more. The design's own `onMwSelect` does exactly this, for the same reason.
  selectMwTrack: (mwSelected) => set({ mwSelected, confirm: null, mwFullSignature: false }),
  setMwSort: (mwSort) => set({ mwSort }),

  setMwIdentityDraft: (eventId, identity) =>
    set((state) => ({ mwIdentityDraft: { ...state.mwIdentityDraft, [eventId]: identity } })),

  setMwFullSignature: (mwFullSignature) => set({ mwFullSignature }),
  setMwTimelineOpen: (mwTimelineOpen) => set({ mwTimelineOpen }),
  setMwRefOpen: (mwRefOpen) => set({ mwRefOpen }),
  setMwFiltersOpen: (mwFiltersOpen) => set({ mwFiltersOpen }),

  addMwFilter: (column) =>
    set((state) => ({
      mwFilters: [...state.mwFilters, newPredicate(columnFor(column))],
      mwFiltersOpen: true,
    })),

  setMwFilter: (id, patch) =>
    set((state) => ({
      mwFilters: state.mwFilters.map((filter) =>
        filter.id === id ? applyPatch(filter, patch) : filter,
      ),
    })),

  removeMwFilter: (id) =>
    set((state) => ({ mwFilters: state.mwFilters.filter((filter) => filter.id !== id) })),

  clearMwFilters: () => set({ mwFilters: [] }),
  setMwRowOpen: (mwRowOpen) => set({ mwRowOpen }),

  raiseMwAttention: () => set({ mwAttention: true }),
  clearMwAttention: () => set((state) => (state.mwAttention ? { mwAttention: false } : state)),

  readIntelTurn: (intelTurn) => set({ intelTurn }),
  toggleIntelGroup: (category) =>
    set((state) => {
      const next = new Set(state.intelClosedGroups);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return { intelClosedGroups: next };
    }),
  setIntelDeltaOpen: (intelDeltaOpen) => set({ intelDeltaOpen }),

  /**
   * A report arrived. Raise the dot only if it is **newer than the last one this
   * seat was shown** and the operator is looking elsewhere.
   *
   * Keyed on the turn rather than on a count, because the report is *replaced* on
   * the socket rather than appended to: a re-render, a resume replay or a second
   * snapshot carrying the same report must not re-light a dot the operator has
   * already cleared.
   */
  noteIntelReport: (turn, looking) =>
    set((state) => {
      if (state.intelSeenTurn !== null && turn <= state.intelSeenTurn) return {};
      return { intelSeenTurn: turn, intelAttention: !looking };
    }),
  clearIntelAttention: () =>
    set((state) => (state.intelAttention ? { intelAttention: false } : state)),

  requestConfirm: (confirm) => set({ confirm }),
  clearConfirm: () => set((state) => (state.confirm === null ? state : { confirm: null })),

  undoLast: () => {
    const stack = get().undo;
    const snapshot = stack[stack.length - 1];
    if (snapshot === undefined) return null;
    set((state) => ({
      builder: snapshot.builder,
      queue: snapshot.queue,
      queueRevision: state.queueRevision + 1,
      undo: stack.slice(0, -1),
      confirm: null,
      armedSlot: null,
      pickQuery: '',
    }));
    return snapshot.label;
  },
}));

/**
 * The undo entry a mutation leaves behind.
 *
 * Returned as a partial rather than pushed by a separate call, so a reducer
 * cannot record the step and then fail to take it — the snapshot and the change
 * are one `set`.
 */
function pushUndo(state: ConsoleState, label: string): { undo: readonly UndoSnapshot[] } {
  const snapshot: UndoSnapshot = { label, builder: state.builder, queue: state.queue };
  return { undo: [...state.undo, snapshot].slice(-UNDO_DEPTH) };
}

/**
 * A predicate row, edited.
 *
 * Changing the **column** clears the operator's typed value, because a boost
 * duration is not an infrared band and carrying `180` across would leave a filter
 * that reads as deliberate and matches by accident. Someone changing their mind
 * about which column they meant has not yet said anything about the value.
 */
function applyPatch(
  filter: FilterPredicate,
  patch: Partial<Omit<FilterPredicate, 'id'>>,
): FilterPredicate {
  const next = { ...filter, ...patch };
  if (patch.column === undefined || patch.column === filter.column) return next;
  const kind = columnByKey(patch.column)?.kind ?? null;
  return { ...next, op: kind === null ? next.op : defaultOpFor(kind), a: '', b: '' };
}

function columnFor(key: string | undefined): RecognitionColumn | undefined {
  return key === undefined ? undefined : columnByKey(key);
}

function targetNames(order: OrderDraft, subjectId: string): boolean {
  const target = order.target;
  switch (target.kind) {
    case 'CONTACT':
      return target.contact === subjectId;
    case 'OWN_ASSET':
      return target.asset === subjectId;
    case 'REGION':
      return target.region === subjectId;
    case 'BAND':
    case 'SLOT':
      return target.band === subjectId;
    case 'MW_EVENT':
      return target.event === subjectId;
    default:
      return false;
  }
}

function persist(state: ConsoleState): void {
  writeRails({
    leftOpen: state.railLeftOpen,
    rightOpen: state.railRightOpen,
    leftWidth: state.railLeftWidth,
    rightWidth: state.railRightWidth,
  });
}

/** Test seam. The store is a module singleton, which is right for an app and wrong for a suite. */
export function resetConsoleStore(): void {
  const rails = defaultRails();
  useConsoleStore.setState({
    seat: null,
    hotkeysOpen: false,
    droppedSnapshots: 0,
    railLeftOpen: rails.leftOpen,
    railRightOpen: rails.rightOpen,
    railLeftWidth: rails.leftWidth,
    railRightWidth: rails.rightWidth,
    ...seatScoped(),
  });
  resetQueueKeys();
  resetPredicateKeys();
}
